import React, { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { z } from "zod";
import { Button } from "@shared/components";
import { FORM_ERROR_MESSAGES, RECOGNITION_STATUS } from "@shared/constants";
import { blurValidationListeners, formSubmitHandler, schemaValidators, useAppForm } from "@shared/form";
import { useLoader } from "@shared/hooks";
import { actions as sharedActions } from "@shared/store";
import { updateRecognition } from "@containers/Recognition/store/actions";

import "./index.scss";

/**
 * api architecture §5.6's redemption precedent, mirrored: `z.string().trim().min(1).max(500)`, REQUIRED
 * when declining. Shared between the Zod `.max()` below and the Textarea's own `maxLength`, never a bare
 * literal repeated in both places -- same discipline DenyRedemptionRequestContainer's own
 * `DENY_REASON_MAX_LENGTH` uses.
 */
export const DECLINE_REASON_MAX_LENGTH = 500;

// Exported so the raw schema can be tested directly against a 501-character string -- the Textarea's own
// `maxLength={DECLINE_REASON_MAX_LENGTH}` attribute makes typing a 501st character unreachable through
// simulated keystrokes, so the `.max()` rule itself needs a direct, DOM-independent test (same reasoning
// DenyRedemptionRequestContainer's own `validationSchema` export records).
export const validationSchema = z.object({
  decision_reason: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.REQUIRED)
    .max(DECLINE_REASON_MAX_LENGTH, `Reason ${FORM_ERROR_MESSAGES.LONG}`),
});

type DeclineReasonFormShape = z.infer<typeof validationSchema>;

const defaultValues: DeclineReasonFormShape = { decision_reason: "" };

interface DeclineRecognitionContainerProps {
  id: number;
  onClose: () => void;
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-53-decline-reason-and-decision-buttons.md PR3: the Review table's required decline-reason
 * overlay, composed from DenyRedemptionRequestContainer's own shape (TanStack Form + Zod, a required
 * max-length-capped reason, the same same-tick double-submit ref) rather than a second mechanism for the
 * same problem the redemption queue already solved in phase 49 W4.
 *
 * Dispatches `updateRecognition.request({ id, status: Declined, decision_reason, cb })` -- the SAME
 * action `RecognitionActionsCell`'s own one-click approve path uses, just with the two fields the api's
 * `.superRefine` requires together. `cb` hides this overlay on success only, never on failure (a failed
 * submit leaves it mounted so the reviewer can retry without re-opening it).
 *
 * 🚨 Lives in its own top-level directory, not nested under any other container's tree -- same
 * independence DenyRedemptionRequestContainer's own comment records (architecture §6.1).
 */
const DeclineRecognitionContainer: FC<DeclineRecognitionContainerProps> = (props) => {
  const { id, onClose } = props;
  const dispatch = useDispatch();

  const { isLoading: isSubmitting } = useLoader({
    name: "DeclineRecognition",
    actionTypes: [updateRecognition],
  });

  /**
   * fix pass F precedent (DenyRedemptionRequestContainer): `isSubmitting` above is redux-derived and
   * only reflects a NEW render -- two clicks inside the same tick both read the same stale
   * `isSubmitting = false` from this render's closure, so the loader alone cannot stop a second PUT.
   * This ref is set synchronously inside `handleSubmit` itself, so the second of two same-tick clicks
   * sees it immediately, before any render happens. Reset once the loader clears WITHOUT a success -- a
   * genuine failure leaves this overlay mounted (only `cb`'s `hideOverlay` unmounts it), and a real retry
   * must not stay blocked by the same-tick guard from the first attempt.
   */
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!isSubmitting) {
      isSubmittingRef.current = false;
    }
  }, [isSubmitting]);

  const handleSubmit = useCallback(
    (values: DeclineReasonFormShape) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      dispatch(
        updateRecognition.request({
          id,
          status: RECOGNITION_STATUS.DECLINED,
          decision_reason: values.decision_reason,
          cb: () => {
            // Dispatched directly, not via the injected `onClose` -- a successful save must always
            // close unconditionally, mirroring DenyRedemptionRequestContainer's own reasoning.
            dispatch(sharedActions.hideOverlay());
          },
        }),
      );
    },
    [dispatch, id],
  );

  const form = useAppForm({
    defaultValues,
    validators: schemaValidators(validationSchema),
    listeners: blurValidationListeners(),
    onSubmit: ({ value }) => handleSubmit(value),
  });

  return (
    <div className="decline-recognition-container">
      <div className="title-wrapper">
        <div className="title">Decline this recognition?</div>
        {/*
          NOT "this can't be undone" -- it can. Declined -> Approved is a legal PUT: there is no
          status-transition guard in `assertRecognitionUpdatable` (api `recognition.guards.ts`), and the
          api deliberately clears `decision_reason` when a row moves back off Declined, which only makes
          sense because the reversal is a real, supported path. Telling a reviewer otherwise would make
          them hesitate over a decision they can take back.

          The reason is reviewer-only: the api gates `decision_reason` on `isReviewer`, the same way it
          gates advisory flags. Deliberately says nothing about the giver seeing it -- today a self-view
          filters Declined rows out entirely (`getVisibleRecognitionStatuses`, api `acl.ts`), so any
          promise here about what they will read would be false.
        */}
        <div className="subtitle">Only reviewers can see the reason.</div>
        <hr />
      </div>
      <form onSubmit={formSubmitHandler(form)}>
        <div className="decline-recognition-content">
          <div className="decline-recognition-reason-wrapper">
            <form.AppField name="decision_reason">
              {(field) => (
                <field.Textarea
                  className="decline-recognition-reason-input"
                  labelTitle="Reason"
                  data-testid="decline-recognition-reason-input"
                  maxLength={DECLINE_REASON_MAX_LENGTH}
                  placeholder="Why is this recognition being declined?"
                />
              )}
            </form.AppField>
          </div>
        </div>
        <div className="decline-recognition-footer">
          <Button type="button" className="subtle auto-width" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" className="accent padding margin-left" disabled={isSubmitting}>
            Decline recognition
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeclineRecognitionContainer;
